import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, Loader2, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Recording modes ────────────────────────────────────────
const MODE = { IDLE: 'idle', RECORDING: 'recording', PROCESSING: 'processing' };

const VoiceOrderButton = ({ menuItems, onItemsMatched, getAuthHeaders }) => {
  const [mode, setMode] = useState(MODE.IDLE);
  const [transcript, setTranscript] = useState('');
  const [lastMatches, setLastMatches] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [listeningMode, setListeningMode] = useState('push'); // 'push' | 'continuous'
  const [wakeWord, setWakeWord] = useState(() => localStorage.getItem('fos_wake_word') || 'hey fos');
  const [showSettings, setShowSettings] = useState(false);
  const [wakeWordDraft, setWakeWordDraft] = useState('');
  const [isWakeWordListening, setIsWakeWordListening] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const feedbackTimerRef = useRef(null);

  // ── Cleanup on unmount ───────────────────────────────────
  useEffect(() => {
    return () => {
      stopRecording(true);
      stopWakeWordListener();
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  // ── Microphone helpers ───────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 }
      });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => processAudio();

      recorder.start(250); // collect every 250ms
      setMode(MODE.RECORDING);
    } catch (err) {
      console.error('[Voice] Mic access denied:', err);
      toast.error('Microphone access denied. Please allow mic permission.');
    }
  }, [menuItems]);

  const stopRecording = useCallback((silent = false) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (!silent) setMode(MODE.PROCESSING);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (silent) setMode(MODE.IDLE);
  }, []);

  // ── Send audio to backend ────────────────────────────────
  const processAudio = useCallback(async () => {
    if (chunksRef.current.length === 0) {
      setMode(MODE.IDLE);
      return;
    }

    setMode(MODE.PROCESSING);

    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = [];

    // Prepare minimal menu payload (only id, name, price)
    const menuPayload = menuItems.map(m => ({
      id: m.id,
      name: m.name,
      price: m.price,
      category: m.category,
    }));

    const formData = new FormData();
    formData.append('audio', blob, 'voice_order.webm');
    formData.append('menu_items_json', JSON.stringify(menuPayload));

    try {
      const res = await axios.post(`${API}/voice/transcribe-and-match`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 15000,
      });

      const { transcript: text, matches, matched_by } = res.data;
      setTranscript(text || '');
      setLastMatches(matches || []);

      if (matches && matches.length > 0) {
        onItemsMatched(matches);
        showFeedbackToast(text, matches, matched_by);
      } else if (text) {
        toast.error(`Couldn't match "${text}" to any menu item`, { duration: 3000 });
      } else {
        toast.error('No speech detected. Try speaking louder.', { duration: 2000 });
      }
    } catch (err) {
      console.error('[Voice] API error:', err);
      const msg = err.response?.data?.detail || 'Voice processing failed';
      toast.error(msg, { duration: 3000 });
    } finally {
      setMode(MODE.IDLE);
      // If continuous mode, restart wake‑word listener
      if (listeningMode === 'continuous') {
        startWakeWordListener();
      }
    }
  }, [menuItems, onItemsMatched, getAuthHeaders, listeningMode]);

  // ── Feedback display ─────────────────────────────────────
  const showFeedbackToast = (text, matches, matchedBy) => {
    const items = matches.map(m => `${m.quantity}x ${m.menu_item_name}`).join(', ');
    toast.success(`Added: ${items}`, {
      description: `"${text}"`,
      duration: 3000,
    });
    setShowFeedback(true);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setShowFeedback(false), 4000);
  };

  // ── Push‑to‑talk handlers ────────────────────────────────
  const handlePushStart = () => {
    if (mode !== MODE.IDLE) return;
    stopWakeWordListener();
    startRecording();
  };

  const handlePushStop = () => {
    if (mode !== MODE.RECORDING) return;
    stopRecording();
  };

  // ── Continuous / wake‑word mode (uses Web Speech API) ────
  const startWakeWordListener = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Continuous mode not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript.toLowerCase().trim();
        if (text.includes(wakeWord.toLowerCase())) {
          // Wake word detected — stop listener and start real recording
          recognition.stop();
          setIsWakeWordListening(false);
          toast.info(`Wake word detected! Listening for your order...`, { duration: 2000 });
          setTimeout(() => {
            startRecording();
            // Auto‑stop after 8 seconds of recording in continuous mode
            setTimeout(() => {
              if (mediaRecorderRef.current?.state === 'recording') {
                stopRecording();
              }
            }, 8000);
          }, 300);
          return;
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('[Voice] Wake word error:', event.error);
      }
    };

    recognition.onend = () => {
      // Restart if still in continuous mode
      if (listeningMode === 'continuous' && isWakeWordListening) {
        try { recognition.start(); } catch {}
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsWakeWordListening(true);
    } catch {}
  }, [wakeWord, listeningMode, startRecording, stopRecording]);

  const stopWakeWordListener = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setIsWakeWordListening(false);
  }, []);

  // Toggle continuous mode
  const toggleContinuousMode = () => {
    if (listeningMode === 'continuous') {
      setListeningMode('push');
      stopWakeWordListener();
    } else {
      setListeningMode('continuous');
      startWakeWordListener();
    }
  };

  // Save wake word
  const saveWakeWord = () => {
    const word = wakeWordDraft.trim().toLowerCase();
    if (word.length < 2) {
      toast.error('Wake word must be at least 2 characters');
      return;
    }
    setWakeWord(word);
    localStorage.setItem('fos_wake_word', word);
    toast.success(`Wake word set to "${word}"`);
    setShowSettings(false);
    // Restart listener if in continuous mode
    if (listeningMode === 'continuous') {
      stopWakeWordListener();
      setTimeout(() => startWakeWordListener(), 300);
    }
  };

  // ── Render ───────────────────────────────────────────────
  const isRecording = mode === MODE.RECORDING;
  const isProcessing = mode === MODE.PROCESSING;

  return (
    <>
      {/* Main mic button */}
      <div className="flex items-center gap-1.5">
        {/* Continuous mode toggle */}
        <button
          data-testid="voice-continuous-toggle"
          onClick={toggleContinuousMode}
          className={`flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 transition-all ${
            listeningMode === 'continuous'
              ? 'bg-green-500/10 border-green-500 text-green-600'
              : 'bg-muted border-border text-muted-foreground hover:border-gray-400'
          }`}
          title={listeningMode === 'continuous' ? `Continuous mode ON (wake: "${wakeWord}")` : 'Enable continuous listening'}
        >
          <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>

        {/* Push‑to‑talk / Mic button */}
        <button
          data-testid="voice-mic-button"
          onMouseDown={listeningMode === 'push' ? handlePushStart : undefined}
          onMouseUp={listeningMode === 'push' ? handlePushStop : undefined}
          onTouchStart={listeningMode === 'push' ? handlePushStart : undefined}
          onTouchEnd={listeningMode === 'push' ? handlePushStop : undefined}
          onClick={listeningMode === 'continuous' && mode === MODE.IDLE ? () => { stopWakeWordListener(); startRecording(); setTimeout(() => { if (mediaRecorderRef.current?.state === 'recording') stopRecording(); }, 8000); } : undefined}
          disabled={isProcessing}
          className={`relative flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 transition-all ${
            isRecording
              ? 'bg-red-500 border-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
              : isProcessing
                ? 'bg-amber-500 border-amber-500 text-white'
                : 'bg-accent/10 border-accent text-accent hover:bg-accent/20'
          }`}
          title={isRecording ? 'Recording... release to process' : isProcessing ? 'Processing...' : 'Hold to speak order'}
        >
          {isProcessing ? (
            <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
          ) : isRecording ? (
            <MicOff className="h-5 w-5 sm:h-6 sm:w-6" />
          ) : (
            <Mic className="h-5 w-5 sm:h-6 sm:w-6" />
          )}
          {/* Pulse ring */}
          {isRecording && (
            <span className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-50" />
          )}
        </button>

        {/* Settings gear */}
        <button
          data-testid="voice-settings-button"
          onClick={() => { setWakeWordDraft(wakeWord); setShowSettings(s => !s); }}
          className="flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Voice settings"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        </button>
      </div>

      {/* Settings popover */}
      {showSettings && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-surface border border-border rounded-xl shadow-xl z-50 p-3">
          <p className="text-xs font-bold text-primary mb-2">Voice Settings</p>
          <div className="space-y-2">
            <label className="text-xs text-secondary">Wake Word</label>
            <div className="flex gap-1.5">
              <input
                data-testid="voice-wake-word-input"
                type="text"
                value={wakeWordDraft}
                onChange={(e) => setWakeWordDraft(e.target.value)}
                placeholder="e.g. hey fos"
                className="flex-1 text-xs px-2 py-1.5 border border-border rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                data-testid="voice-save-wake-word"
                onClick={saveWakeWord}
                className="px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent/90"
              >
                Save
              </button>
            </div>
            <p className="text-[10px] text-secondary">Say this word to activate voice input in continuous mode</p>
          </div>
          <div className="mt-3 pt-2 border-t border-border">
            <p className="text-[10px] text-secondary">
              <strong>Push‑to‑talk:</strong> Hold the mic button to record<br/>
              <strong>Continuous:</strong> Say "{wakeWord}" to trigger recording
            </p>
          </div>
        </div>
      )}

      {/* Feedback banner */}
      {showFeedback && lastMatches.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 mx-2 bg-green-50 border border-green-200 rounded-lg p-2 shadow-md z-40 animate-in fade-in slide-in-from-top-2 duration-300">
          <p className="text-[10px] text-green-700 font-medium mb-0.5">Voice: "{transcript}"</p>
          <div className="flex flex-wrap gap-1">
            {lastMatches.map((m, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-[10px] font-medium">
                {m.quantity}x {m.menu_item_name}
                <span className="text-green-500 text-[8px]">({Math.round(m.confidence * 100)}%)</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default VoiceOrderButton;
